import { devLog, devError } from '../utils/devLogger';
import React, { useEffect, useState } from 'react';
import { Card, Button, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { useAuth } from '../auth/AuthContext';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase';

const SessionPoll = () => {
  const { currentUser, userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [userPoll, setUserPoll] = useState(null);
  const [isEligible, setIsEligible] = useState(false);
  const [eligibilityChecked, setEligibilityChecked] = useState(false);
  const [showOptions, setShowOptions] = useState(true);
  const [activePoll, setActivePoll] = useState(null);
  const [pollOptions, setPollOptions] = useState([]);
  const [pollEventId, setPollEventId] = useState('');

  useEffect(() => {
    if (currentUser && isEligible && pollEventId) {
      checkUserPoll();
    } else if (currentUser && eligibilityChecked) {
      setLoading(false);
    }
  }, [currentUser, isEligible, eligibilityChecked, pollEventId]);

  useEffect(() => {
    const checkEligibility = async () => {
      if (!currentUser || userProfile?.role !== 'learner') {
        setIsEligible(false);
        setEligibilityChecked(true);
        return;
      }

      try {
        const pollsSnapshot = await getDocs(
          query(
            collection(db, 'events'),
            where('type', '==', 'session_poll'),
            where('status', '==', 'active')
          )
        );
        if (pollsSnapshot.empty) {
          setActivePoll(null);
          setPollOptions([]);
          setPollEventId('');
          setIsEligible(false);
          setEligibilityChecked(true);
          return;
        }

        const pollDoc = pollsSnapshot.docs
          .map(docSnap => ({ id: docSnap.id, ...docSnap.data() }))
          .sort((a, b) => {
            const aTime = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : new Date(a.createdAt || 0).getTime();
            const bTime = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : new Date(b.createdAt || 0).getTime();
            return bTime - aTime;
          })[0];
        if (!pollDoc) {
          setActivePoll(null);
          setPollOptions([]);
          setPollEventId('');
          setIsEligible(false);
          setEligibilityChecked(true);
          return;
        }
        const pollData = pollDoc;
        setActivePoll(pollData);
        setPollOptions(Array.isArray(pollData.options) ? pollData.options : []);
        setPollEventId(pollData.id);

        const allowedCohortIds = Array.isArray(pollData.allowedCohortIds) ? pollData.allowedCohortIds : [];
        const allowedCohortNames = Array.isArray(pollData.allowedCohortNames) ? pollData.allowedCohortNames : [];

        if (!allowedCohortIds.length && !allowedCohortNames.length) {
          setIsEligible(true);
          setEligibilityChecked(true);
          return;
        }

        if (userProfile?.cohortId && allowedCohortIds.includes(userProfile.cohortId)) {
          setIsEligible(true);
          setEligibilityChecked(true);
          return;
        }

        if (userProfile?.cohortName && allowedCohortNames.includes(userProfile.cohortName)) {
          setIsEligible(true);
          setEligibilityChecked(true);
          return;
        }
      } catch (err) {
        devError('Error loading active poll:', err);
        setActivePoll(null);
        setPollOptions([]);
        setPollEventId('');
        setIsEligible(false);
        setEligibilityChecked(true);
        return;
      }

      // If no direct cohort ID/name match was found above, learner is not eligible.
      setIsEligible(false);
      setEligibilityChecked(true);
    };

    checkEligibility();
  }, [currentUser, userProfile?.role, userProfile?.cohortId, userProfile?.cohortName]);

  const checkUserPoll = async () => {
    try {
      setLoading(true);
      const functions = getFunctions();
      const getPollStatus = httpsCallable(functions, 'getSessionPollStatus');
      const response = await getPollStatus({ eventId: pollEventId });
      const status = response.data || {};

      if (status.exists && status.poll) {
        setUserPoll(status.poll);
        setShowOptions(false);
      } else {
        setUserPoll(null);
        setShowOptions(true);
      }
    } catch (err) {
      devError('Error checking poll status:', err);
      setError('Could not check your poll status. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (optionId) => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const selectedOption = pollOptions.find(option => option.id === optionId);
      if (!selectedOption) {
        throw new Error('Invalid option selected.');
      }

      const pollData = {
        eventId: pollEventId,
        optionId: selectedOption.id,
        optionLabel: selectedOption.label,
        studentId: currentUser.uid,
        studentName: userProfile?.displayName || currentUser.email,
        studentEmail: userProfile?.email,
        cohortId: userProfile?.cohortId || null,
        cohortName: userProfile?.cohortName || null,
        programId: userProfile?.programId || null
      };
      const functions = getFunctions();
      const submitPoll = httpsCallable(functions, 'submitSessionPoll');
      await submitPoll(pollData);
      setSuccess('Your response has been recorded.');
      await checkUserPoll();
    } catch (err) {
      devError('Error submitting poll response:', err);
      setError('Failed to submit your response. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!eligibilityChecked || !isEligible || !pollEventId) {
    return null;
  }

  return (
    <Card className="shadow-sm mb-4">
      <Card.Header className="bg-secondary text-white">
        <h6 className="mb-0">{activePoll?.title || 'Session Time Poll'}</h6>
      </Card.Header>
      <Card.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        {loading ? (
          <div className="text-center">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">Loading poll...</p>
          </div>
        ) : (
          <>
            {userPoll && !showOptions && (
              <Alert variant="info">
                <p className="mb-2">
                  <strong>Your selection:</strong>
                </p>
                <p className="mb-0">{userPoll.optionLabel}</p>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="mt-3"
                  onClick={() => setShowOptions(true)}
                >
                  Change selection
                </Button>
              </Alert>
            )}

            {showOptions && (
              <>
                <p>Please select your preferred session time:</p>
                <Row>
                  {pollOptions.map(option => (
                    <Col md={6} key={option.id} className="mb-3">
                      <Card className="h-100">
                        <Card.Body className="d-flex flex-column justify-content-between">
                          <div>
                            <Card.Title className="h6">{option.label}</Card.Title>
                          </div>
                          <Button
                            variant="primary"
                            onClick={() => handleVote(option.id)}
                            disabled={loading}
                          >
                            Select this time
                          </Button>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default SessionPoll;
